/**
 * EngineTelemetryPanel — real-time readout of `/health` and `/metrics`
 * on the CommandPage. Polls via TanStack Query; renders an "offline"
 * dim when no server is reachable (mock-mode dev / network blip),
 * a "degraded"/"unhealthy" accent when health checks fail, and the
 * key counters + p50/p95 latency otherwise.
 *
 * The panel is purely additive to the existing mock-backed stat
 * cards; it does not drive any gated tests.
 */

import { useTranslation } from 'react-i18next';

import { cn } from '@/utils/cn';
import type { EngineHealth, EngineMetrics } from '@/api/types';
import { useEngineHealth, useEngineMetrics } from '@/hooks/useCommand';
import type { Locale } from '@/i18n/config';
import { useLocaleStore } from '@/stores/localeStore';
import { formatNumber } from '@/utils/format';

/** Counters surfaced on the panel. Anything not present in the
 *  parsed metrics renders as `—`. Lineup reflects the audit events
 *  the engine currently emits; expand when new counters land. */
const COUNTERS: Array<{ key: string; labelKey: string }> = [
  { key: 'gi_created_total',         labelKey: 'gi_created' },
  { key: 'gi_verified_total',        labelKey: 'gi_verified' },
  { key: 'blueprint_created_total',  labelKey: 'blueprint_created' },
  { key: 'blueprint_instantiated_total', labelKey: 'blueprint_instantiated' },
  { key: 'consent_issued_total',     labelKey: 'consent_issued' },
  { key: 'consent_revoked_total',    labelKey: 'consent_revoked' },
  { key: 'zk_verified_total',        labelKey: 'zk_verified' },
  { key: 'attestations_total',       labelKey: 'attestations' },
];

export function EngineTelemetryPanel() {
  const { t } = useTranslation('command');
  const locale = useLocaleStore((s) => s.locale);
  const health = useEngineHealth();
  const metrics = useEngineMetrics();

  const offline = health.data === null && metrics.data === null;

  return (
    <section
      aria-labelledby="telemetry-heading"
      className="flex flex-col gap-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2
          id="telemetry-heading"
          className="text-sm font-semibold uppercase tracking-wider text-ink-muted"
        >
          {t('sections.engine_telemetry')}
        </h2>
        <HealthBadge
          health={health.data ?? null}
          loading={health.isPending && !offline}
          offline={offline}
        />
      </div>

      <div
        className={cn(
          'rounded-lg border border-border bg-surface p-5 shadow-subtle',
          offline && 'opacity-60',
        )}
      >
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {COUNTERS.map(({ key, labelKey }) => (
            <CounterCell
              key={key}
              label={t(`telemetry.counter.${labelKey}`)}
              value={metrics.data?.scalars[key]}
              locale={locale}
            />
          ))}
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 border-t border-border pt-4 sm:grid-cols-2">
          <LatencyCell
            label={t('telemetry.latency.p50')}
            seconds={metrics.data?.histograms['request_duration_seconds']?.p50_seconds}
          />
          <LatencyCell
            label={t('telemetry.latency.p95')}
            seconds={metrics.data?.histograms['request_duration_seconds']?.p95_seconds}
          />
        </div>

        {health.data?.checks.length ? (
          <ul className="mt-4 flex flex-col gap-1 border-t border-border pt-4 text-xs">
            {health.data.checks.map((c) => (
              <li
                key={c.name}
                className="flex items-center justify-between gap-2"
              >
                <span className="font-mono text-ink-muted">{c.name}</span>
                <span className={statusColour(c.status)}>{c.status}</span>
              </li>
            ))}
          </ul>
        ) : null}

        {offline ? (
          <p className="mt-4 border-t border-border pt-4 text-xs text-ink-subtle">
            {t('telemetry.offline_hint')}
          </p>
        ) : null}
      </div>
    </section>
  );
}

function HealthBadge({
  health,
  loading,
  offline,
}: {
  health: EngineHealth | null;
  loading: boolean;
  offline: boolean;
}) {
  const { t } = useTranslation('command');
  if (offline) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-raised px-2 py-0.5 text-[11px] font-medium text-ink-subtle">
        <Dot className="bg-ink-subtle" />
        {t('telemetry.offline')}
      </span>
    );
  }
  if (loading || !health) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-raised px-2 py-0.5 text-[11px] font-medium text-ink-muted">
        <Dot className="bg-ink-muted animate-pulse" />
        {t('telemetry.probing')}
      </span>
    );
  }
  const { cls, dot } = badgeClasses(health.status);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium',
        cls,
      )}
    >
      <Dot className={dot} />
      {t(`telemetry.status.${health.status}`)}
      <span className="ms-1 font-mono text-[10px] opacity-80">v{health.version}</span>
    </span>
  );
}

function Dot({ className }: { className?: string }) {
  return <span aria-hidden="true" className={cn('h-1.5 w-1.5 rounded-full', className)} />;
}

function CounterCell({
  label,
  value,
  locale,
}: {
  label: string;
  value: number | undefined;
  locale: Locale;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-mono text-xl font-semibold tabular-nums text-ink">
        {value === undefined ? '—' : formatNumber(Math.round(value), locale)}
      </span>
      <span className="text-[10px] uppercase tracking-wider text-ink-muted">
        {label}
      </span>
    </div>
  );
}

function LatencyCell({
  label,
  seconds,
}: {
  label: string;
  seconds: number | undefined;
}) {
  const { t } = useTranslation('command');
  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-mono text-xl font-semibold tabular-nums text-ink">
        {seconds === undefined || !Number.isFinite(seconds)
          ? '—'
          : t('telemetry.latency.ms', { value: Math.round(seconds * 1000) })}
      </span>
      <span className="text-[10px] uppercase tracking-wider text-ink-muted">
        {label}
      </span>
    </div>
  );
}

function badgeClasses(status: EngineHealth['status']): { cls: string; dot: string } {
  switch (status) {
    case 'healthy':
      return {
        cls: 'border-success/30 bg-success-soft text-success',
        dot: 'bg-success',
      };
    case 'degraded':
      return {
        cls: 'border-warn/30 bg-warn-soft text-warn',
        dot: 'bg-warn',
      };
    case 'unhealthy':
      return {
        cls: 'border-danger/30 bg-danger/10 text-danger',
        dot: 'bg-danger',
      };
    default:
      return {
        cls: 'border-border bg-surface-raised text-ink-muted',
        dot: 'bg-ink-muted',
      };
  }
}

function statusColour(status: EngineHealth['checks'][number]['status']): string {
  switch (status) {
    case 'healthy':
      return 'text-success';
    case 'degraded':
      return 'text-warn';
    case 'unhealthy':
      return 'text-danger';
    default:
      return 'text-ink-subtle';
  }
}

/** Minimal smoke-test fixture used in dev without a running server —
 *  lets you eyeball the layout by calling this directly. Not
 *  currently wired. */
export function __devSampleMetrics(): EngineMetrics {
  return {
    scalars: {
      gi_created_total: 42,
      gi_verified_total: 210,
      consent_issued_total: 7,
      blueprint_created_total: 3,
    },
    histograms: {
      request_duration_seconds: {
        count: 300,
        sum: 12.4,
        p50_seconds: 0.028,
        p95_seconds: 0.181,
      },
    },
  };
}
